/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, FormEvent, ChangeEvent } from "react";

// @material-ui core components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Settings page components
import FormField from "layouts/pages/account/components/FormField";

// Auth context
import { useAuth } from "context/auth/authContext";

function BasicInfo(): JSX.Element {
  const { user, updateProfile, error, clearError } = useAuth();
  
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  
  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setEmail(user.email || "");
      setUsername(user.username || "");
    }
  }, [user]);
  
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!firstName || !lastName || !email) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await updateProfile({
        firstName,
        lastName,
        email
      });
      
      setSuccessMessage("Profile updated successfully!");
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    } catch (err) {
      console.error("Profile update error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card id="basic-info" sx={{ overflow: "visible" }}>
      <MDBox p={3}>
        <MDTypography variant="h5">Basic Info</MDTypography>
      </MDBox>
      
      {error && (
        <MDBox mx={3} mb={2}>
          <Alert severity="error" onClose={clearError}>
            {error}
          </Alert>
        </MDBox>
      )}
      
      {successMessage && (
        <MDBox mx={3} mb={2}>
          <Alert severity="success">
            {successMessage}
          </Alert>
        </MDBox>
      )}
      
      <MDBox component="form" pb={3} px={3} onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormField 
              label="First Name" 
              placeholder="John" 
              value={firstName}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField 
              label="Last Name" 
              placeholder="Doe" 
              value={lastName}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              label="Email"
              placeholder="example@email.com"
              inputProps={{ type: "email" }}
              value={email}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              label="Username"
              placeholder="username"
              value={username}
              InputProps={{ readOnly: true }}
              // Username cannot be changed once set
            />
          </Grid>
        </Grid>
        
        <MDBox mt={4} display="flex" justifyContent="flex-end">
          <MDButton 
            variant="gradient" 
            color="dark" 
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Save Changes"
            )}
          </MDButton>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default BasicInfo;
